import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { LngLatBounds } from 'mapbox-gl';
import type { GradeReference } from '../domain/Roaddata';
import type { Session, SessionId } from '../domain/Session';
import type { ExecutionStatus, OrganizationNetwork } from './ExternalNetworkMapPort';
import type { MapStyle } from '../../types';

export const moduleName = 'SESSIONS_MAP';

export type SessionShownOnMap = { sessionId: SessionId; shown: boolean };
export type OrganizationZoomedOnMap = { toggle: boolean };

type State = {
    network: OrganizationNetwork | null;
    // network data of single sessions, kept apart from `network` so it never replaces the whole organization's
    sessionNetworks: Record<SessionId, OrganizationNetwork>;
    networkExecutionStatuses: Array<ExecutionStatus> | null;
    networkExecutionsNeedRefresh: Array<{ sessionId: string; refresh: boolean }>;
    sessionsIds: SessionId[];
    sessionsShownOnMap: SessionId[];
    selectedGradeReference: GradeReference;
    currentMapBounds: LngLatBounds | null;
    requestedMapBounds: [number, number, number, number] | null;
    renderedSessionsIds: SessionId[];
    selectedMapStyle: MapStyle;
    displayPr: boolean;
    displayRoads: boolean;
    displayDegradations: boolean;
    highlightedRoadSectionLine: { line: [number, number][]; overallRate?: number | null } | null;
};

const initialState: State = {
    network: null,
    sessionNetworks: {},
    networkExecutionStatuses: null,
    networkExecutionsNeedRefresh: [],
    sessionsIds: [],
    sessionsShownOnMap: [],
    selectedGradeReference: 'AGGREGATE',
    currentMapBounds: null,
    requestedMapBounds: null,
    renderedSessionsIds: [],
    selectedMapStyle: 'STREETS',
    displayPr: false,
    displayRoads: true,
    displayDegradations: false,
    highlightedRoadSectionLine: null,
};

const networkMapSlice = createSlice({
    name: moduleName,
    initialState,
    reducers: {
        resetStarted(state: State) {
            state.network = null;
        },
        networkMapLoaded(state: State, action: PayloadAction<OrganizationNetwork>) {
            state.network = action.payload;
        },
        sessionNetworkLoaded(state: State, action: PayloadAction<{ sessionId: SessionId; network: OrganizationNetwork }>) {
            state.sessionNetworks[action.payload.sessionId] = action.payload.network;
        },
        executionStatusesRefreshed(state: State, action: PayloadAction<Array<ExecutionStatus>>) {
            state.networkExecutionsNeedRefresh = (state.networkExecutionStatuses ?? []).map((oldStatus) => {
                const newStatus = action.payload.find((ns) => ns.sessionId === oldStatus.sessionId);

                return {
                    sessionId: oldStatus.sessionId,
                    refresh: oldStatus.status !== newStatus?.status && newStatus?.status === 'SUCCEED',
                };
            });
            state.networkExecutionStatuses = action.payload;
        },
        initSessionsToggledOnMap(state: State, action: PayloadAction<Session[]>) {
            if (state.sessionsIds.length === 0) {
                state.sessionsIds = action.payload.flatMap((session) => session.id);
                state.sessionsShownOnMap = action.payload.flatMap((session) => session.id);
            }
        },
        sessionToggledOnMap(state: State, action: PayloadAction<SessionShownOnMap>) {
            const index = state.sessionsShownOnMap.findIndex((sessionId) => sessionId === action.payload.sessionId);
            if (index === -1 && action.payload.shown) {
                state.sessionsShownOnMap = [...state.sessionsShownOnMap, action.payload.sessionId];
            }
            if (index > -1 && !action.payload.shown) {
                state.sessionsShownOnMap.splice(index, 1);
            }
        },
        allSessionsToggledOnMap(state: State, action: PayloadAction<boolean>) {
            if (action.payload) {
                state.sessionsShownOnMap = [...state.sessionsIds];
            }
            if (!action.payload) {
                state.sessionsShownOnMap = [];
            }
        },
        gradeReferenceSelectedOnMap(state: State, action: PayloadAction<GradeReference>) {
            state.selectedGradeReference = action.payload;
        },
        selectedMapStyle(state: State, action: PayloadAction<MapStyle>) {
            state.selectedMapStyle = action.payload;
        },
        displayPr(state: State, action: PayloadAction<boolean>) {
            state.displayPr = action.payload;
        },
        displayRoads(state: State, action: PayloadAction<boolean>) {
            state.displayRoads = action.payload;
        },
        displayDegradations(state: State, action: PayloadAction<boolean>) {
            state.displayDegradations = action.payload;
        },
        currentMapBoundsStored(state: State, action: PayloadAction<LngLatBounds>) {
            state.currentMapBounds = action.payload;
            state.requestedMapBounds = null;
        },
        requestMapBounds(state: State, action: PayloadAction<[number, number, number, number] | null>) {
            state.requestedMapBounds = action.payload;
        },
        renderedSessionsIdsUpdated(state: State, action: PayloadAction<SessionId[]>) {
            state.renderedSessionsIds = action.payload;
        },
        roadSectionLineHighlighted(
            state: State,
            action: PayloadAction<{ line: [number, number][]; overallRate?: number | null } | null>,
        ) {
            state.highlightedRoadSectionLine = action.payload;
        },
    },
});

export const {
    resetStarted,
    networkMapLoaded,
    sessionNetworkLoaded,
    initSessionsToggledOnMap,
    sessionToggledOnMap,
    allSessionsToggledOnMap,
    gradeReferenceSelectedOnMap,
    currentMapBoundsStored,
    requestMapBounds,
    renderedSessionsIdsUpdated,
    selectedMapStyle,
    displayPr,
    displayRoads,
    displayDegradations,
    executionStatusesRefreshed,
    roadSectionLineHighlighted,
} = networkMapSlice.actions;
export const networkMapReducer = networkMapSlice.reducer;
